// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { IdentificationCard } from "@phosphor-icons/react";
import { Button, Heading, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

export const ASK_V2_WELCOME_CHIPS = [
  { id: "know", labelKey: "askV2.home.welcome.know" },
  { id: "personalize", labelKey: "askV2.home.welcome.personalize" },
  { id: "canDo", labelKey: "askV2.home.welcome.canDo" },
] as const;

type WelcomeBriefingProps = {
  disabled?: boolean;
  onSelect: (prompt: string) => void;
  /** Personalized subheading from christo; null keeps the translated default. */
  title?: string | null;
  /** Personalized summary from christo; null keeps the translated default. */
  summary?: string | null;
  /** Skeleton the title/summary until christo answers; chips stay visible. */
  pending?: boolean;
};

function WelcomeCopySkeleton() {
  return (
    <div
      data-testid="ask-v2-welcome-copy-skeleton"
      className="flex w-full flex-col items-center gap-3"
      aria-hidden="true"
    >
      <div className="h-7 w-[min(100%,22rem)] animate-pulse rounded-md bg-puntt-neutral-gray-6" />
      <div className="flex w-full max-w-xl flex-col items-center gap-2">
        <div className="h-4 w-full animate-pulse rounded bg-puntt-neutral-gray-6" />
        <div className="h-4 w-4/5 animate-pulse rounded bg-puntt-neutral-gray-6" />
      </div>
    </div>
  );
}

function personalizedOrDefault(
  personalized: string | null | undefined,
  fallback: string,
): { text: string; personalized: boolean } {
  const trimmed = personalized?.trim();
  return trimmed
    ? { text: trimmed, personalized: true }
    : { text: fallback, personalized: false };
}

export function WelcomeBriefing({
  disabled = false,
  onSelect,
  title = null,
  summary = null,
  pending = false,
}: WelcomeBriefingProps) {
  const { t } = useTranslation("ui");
  const heading = personalizedOrDefault(title, t("askV2.home.welcome.title"));
  const body = personalizedOrDefault(summary, t("askV2.home.welcome.summary"));

  return (
    <section
      data-testid="ask-v2-welcome"
      data-personalized={
        heading.personalized || body.personalized ? "true" : "false"
      }
      data-pending={pending ? "true" : "false"}
      aria-busy={pending}
      aria-label={pending ? t("askV2.home.welcome.title") : undefined}
      className="flex w-full max-w-3xl flex-col items-center gap-3 text-center"
    >
      {pending ? (
        <WelcomeCopySkeleton />
      ) : (
        <>
          <Heading as="h2" size="5" data-testid="ask-v2-welcome-title">
            {heading.text}
          </Heading>
          <Text
            as="p"
            size="3"
            className="text-puntt-neutral-gray-11"
            data-testid="ask-v2-welcome-summary"
          >
            {body.text}
          </Text>
        </>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
        {ASK_V2_WELCOME_CHIPS.map((chip) => {
          const label = t(chip.labelKey);
          return (
            <Button
              key={chip.id}
              type="button"
              size="3"
              variant="outline"
              data-testid={`ask-v2-welcome-chip-${chip.id}`}
              data-chip={chip.id}
              disabled={disabled}
              onClick={() => onSelect(label)}
            >
              <IdentificationCard />
              {label}
            </Button>
          );
        })}
      </div>
    </section>
  );
}
